import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Flame, Pencil, Check } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { api } from "@/lib/api";
import PawSpinner from "@/components/PawSpinner";
import PomEmpty from "@/components/PomEmpty";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

export default function Home() {
  const { user, userId, loading, updateName } = useUser();
  const navigate = useNavigate();

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  const startEditName = () => {
    setNameDraft(user?.name || "Adventurer");
    setEditingName(true);
  };

  const saveName = async () => {
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== user?.name) {
      await updateName(trimmed);
    }
    setEditingName(false);
  };

  const { data: characters = [] } = useQuery({
    queryKey: ["characters", userId],
    queryFn: () => api.listCharacters(userId).then((r) => r.data),
    enabled: !!userId,
  });

  const { data: monthlyData } = useQuery({
    queryKey: ["monthly", userId],
    queryFn: () => api.getMonthlyCheck(userId).then((r) => r.data),
    enabled: !!userId,
  });

  const { data: memories = [] } = useQuery({
    queryKey: ["memories", userId],
    queryFn: () => api.listMemories(userId).then((r) => r.data),
    enabled: !!userId,
  });

  const { data: pendingItems = [] } = useQuery({
    queryKey: ["pending-items", userId],
    queryFn: () => api.listBucketItems(userId, null, "pending").then((r) => r.data),
    enabled: !!userId,
  });

  const randomPending = useMemo(
    () =>
      pendingItems.length > 0
        ? pendingItems[Math.floor(Math.random() * pendingItems.length)]
        : null,
    [pendingItems]
  );

  if (loading)
    return (
      <div className="flex justify-center py-20">
        <PawSpinner size="lg" />
      </div>
    );

  const recentMemories = memories.slice(0, 2);

  return (
    <div className="space-y-8 py-2" data-testid="home-screen">
      {/* Greeting */}
      {editingName ? (
        <div className="flex items-center gap-2" data-testid="home-name-edit">
          <span className="font-heading text-[28px] text-text-primary tracking-tight">
            Hi
          </span>
          <input
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveName();
              if (e.key === "Escape") setEditingName(false);
            }}
            maxLength={30}
            className="font-heading text-[28px] text-text-primary bg-transparent border-b-2 border-blush focus:outline-none w-40 tracking-tight"
            data-testid="home-name-input"
          />
          <button
            onClick={saveName}
            className="w-9 h-9 rounded-full bg-blush hover:bg-blush-dark flex items-center justify-center transition-colors"
            data-testid="home-name-save-btn"
          >
            <Check size={16} className="text-white" />
          </button>
        </div>
      ) : (
        <button
          onClick={startEditName}
          className="group flex items-center gap-2 text-left"
          data-testid="home-greeting"
        >
          <h1 className="font-heading text-[28px] text-text-primary tracking-tight">
            Hi {user?.name || "Adventurer"} 🌸
          </h1>
          <Pencil
            size={16}
            className="text-text-secondary opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid="home-name-edit-icon"
          />
        </button>
      )}

      {/* Monthly nudge banner */}
      {monthlyData?.show_nudge && (
        <div
          className="bg-blush/10 border border-blush/20 rounded-card p-4 text-sm text-text-primary animate-slide-up"
          data-testid="monthly-nudge-banner"
        >
          You haven't tried anything new this month yet. Pick something!
          <a
            href="#nudge"
            className="block mt-2 text-blush-dark font-medium hover:underline"
          >
            Pick something &rarr;
          </a>
        </div>
      )}

      {/* Character Row */}
      <section>
        <h3 className="font-heading text-lg text-text-primary mb-3">
          Mama's Characters
        </h3>
        <div
          className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide"
          data-testid="character-row"
        >
          {characters.map((char) => (
            <Link
              key={char.id}
              to="/characters"
              className="flex flex-col items-center gap-1.5 shrink-0"
              data-testid={`character-avatar-${char.id}`}
            >
              <Avatar className="w-20 h-20 border-2 border-border-pink">
                {(char.generated_preview_url || char.reference_photos?.[0]) && (
                  <AvatarImage
                    src={api.getFileUrl(char.generated_preview_url || char.reference_photos[0])}
                    alt={char.name}
                  />
                )}
                <AvatarFallback className="bg-cream text-blush text-lg font-heading">
                  {char.name?.[0]}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs text-text-secondary font-medium">
                {char.name}
              </span>
            </Link>
          ))}
          <Link
            to="/characters/new"
            className="flex flex-col items-center gap-1.5 shrink-0"
            data-testid="add-character-btn"
          >
            <div className="w-20 h-20 rounded-full border-2 border-dashed border-border-pink flex items-center justify-center bg-cream hover:bg-blush/5 transition-colors">
              <Plus className="text-blush" size={24} />
            </div>
            <span className="text-xs text-text-secondary">Add</span>
          </Link>
        </div>
      </section>

      {/* Nudge Card */}
      <section
        id="nudge"
        className="bg-white rounded-card shadow-soft p-6 border-l-4 border-gold card-hover"
        data-testid="nudge-card"
      >
        <p className="text-sm text-text-secondary mb-1">This month's nudge</p>
        <p className="text-text-primary font-medium mb-3">
          Pick one thing and do it
        </p>
        {randomPending ? (
          <div className="mb-4">
            <span className="text-sm text-text-secondary">How about: </span>
            <span className="text-sm text-text-primary font-semibold">
              {randomPending.title}
            </span>
          </div>
        ) : (
          <p className="text-sm text-text-secondary mb-4">
            Add some adventures to your list first!
          </p>
        )}
        <button
          onClick={() =>
            randomPending
              ? navigate(`/complete/${randomPending.id}`)
              : navigate("/bucketlist")
          }
          className="bg-blush hover:bg-blush-dark text-white px-6 py-2.5 rounded-pill text-sm font-medium transition-colors"
          data-testid="nudge-cta-btn"
        >
          {randomPending ? "Let's do this" : "Add adventures"}
        </button>
      </section>

      {/* Streak */}
      {monthlyData?.streak > 0 && (
        <div
          className="inline-flex items-center gap-1.5 bg-gold/10 text-gold px-4 py-1.5 rounded-pill text-sm font-medium"
          data-testid="streak-counter"
        >
          <Flame size={16} />
          {monthlyData.streak} month{monthlyData.streak !== 1 ? "s" : ""} in a
          row
        </div>
      )}

      {/* Recent Memories */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg text-text-primary">
            Recent Memories
          </h3>
          {memories.length > 0 && (
            <Link
              to="/memories"
              className="text-sm text-blush-dark font-medium hover:underline"
              data-testid="see-all-memories-link"
            >
              See all
            </Link>
          )}
        </div>
        {recentMemories.length > 0 ? (
          <div className="grid grid-cols-2 gap-4">
            {recentMemories.map((memory) => (
              <Link
                key={memory.id}
                to="/memories"
                className="bg-white rounded-card shadow-soft overflow-hidden card-hover"
                data-testid={`recent-memory-${memory.id}`}
              >
                {(memory.generated_photo_urls?.[0] || memory.generated_photo_url || memory.photo_url) && (
                  <div className="aspect-square bg-cream">
                    <img
                      src={api.getFileUrl(
                        memory.generated_photo_urls?.[0] || memory.generated_photo_url || memory.photo_url
                      )}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-3">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {memory.bucket_item?.title || "Memory"}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {new Date(memory.completed_at).toLocaleDateString()}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <PomEmpty message="Waimbow is waiting for your first adventure" />
        )}
      </section>
    </div>
  );
}
